(() => {
  const selector = '[data-mp-award-carousel]';

  const initialise = (scope = document) => {
    const roots = [];

    if (scope instanceof Element && scope.matches(selector)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(selector));

    roots.forEach((root) => {
      if (root.dataset.mpAwardReady === 'true') return;

      const viewport = root.querySelector('[data-mp-award-viewport]');
      const track = root.querySelector('[data-mp-award-track]');
      const items = track ? Array.from(track.children) : [];
      const controls = root.querySelector('[data-mp-award-controls]');
      const previous = root.querySelector('[data-mp-award-previous]');
      const next = root.querySelector('[data-mp-award-next]');
      const status = root.querySelector('[data-mp-award-status]');
      const autoplayControl = root.querySelector('[data-mp-award-autoplay]');
      const pauseIcon = autoplayControl?.querySelector('[data-mp-award-pause-icon]');
      const playIcon = autoplayControl?.querySelector('[data-mp-award-play-icon]');

      if (!viewport || !track || items.length === 0) return;

      root.dataset.mpAwardReady = 'true';
      root.style.setProperty('--mp-award-item-count', String(items.length));

      const visibleItems = Math.min(3, items.length);
      const pageCount = Math.ceil(items.length / visibleItems);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const paused = new Set();
      let currentPage = 0;
      let autoplayTimer = 0;
      let scrollTimer = 0;
      let announcementTimer = 0;
      let announcementFrame = 0;
      let resizeObserver;
      let intersectionObserver;
      let destroyed = false;

      const pageStartIndex = (page) => Math.min(page * visibleItems, items.length - visibleItems);
      const snapIndexes = new Set(Array.from({ length: pageCount }, (_, page) => pageStartIndex(page)));
      items.forEach((item, index) => {
        item.style.scrollSnapAlign = snapIndexes.has(index) ? 'start' : 'none';
      });

      const pageLeft = (page) => {
        const item = items[pageStartIndex(page)];
        const origin = items[0]?.offsetLeft || 0;
        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        return Math.min(Math.max(0, item ? item.offsetLeft - origin : 0), maxScroll);
      };

      const updateStatus = (announce = false) => {
        if (!status) return;
        const start = pageStartIndex(currentPage) + 1;
        const end = Math.min(start + visibleItems - 1, items.length);
        const message = `${start}–${end} von ${items.length}`;

        if (announce) {
          window.clearTimeout(announcementTimer);
          announcementTimer = 0;
          window.cancelAnimationFrame(announcementFrame);
          status.setAttribute('aria-live', 'polite');
          status.textContent = '';
          announcementFrame = window.requestAnimationFrame(() => {
            announcementFrame = 0;
            if (destroyed) return;
            status.textContent = message;
            announcementTimer = window.setTimeout(() => {
              status.setAttribute('aria-live', 'off');
              announcementTimer = 0;
            }, 1200);
          });
        } else {
          if (!announcementTimer) status.setAttribute('aria-live', 'off');
          status.textContent = message;
        }
      };

      const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = 0;
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (destroyed || pageCount <= 1 || reducedMotion.matches || paused.size > 0 || document.hidden) return;

        autoplayTimer = window.setInterval(() => {
          currentPage = (currentPage + 1) % pageCount;
          viewport.scrollTo({ left: pageLeft(currentPage), behavior: 'smooth' });
          updateStatus(false);
        }, 5000);
      };

      const goToPage = (page, announce = true) => {
        currentPage = (page + pageCount) % pageCount;
        viewport.scrollTo({
          left: pageLeft(currentPage),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
        updateStatus(announce);
        startAutoplay();
      };

      const pauseFor = (reason) => {
        paused.add(reason);
        stopAutoplay();
      };

      const resumeFrom = (reason) => {
        paused.delete(reason);
        startAutoplay();
      };

      const updateAutoplayControl = () => {
        if (!autoplayControl) return;
        const manuallyPaused = paused.has('manual');
        autoplayControl.setAttribute('aria-pressed', String(manuallyPaused));
        autoplayControl.setAttribute('aria-label', manuallyPaused ? 'Automatische Wiedergabe fortsetzen' : 'Automatische Wiedergabe pausieren');
        if (pauseIcon) pauseIcon.hidden = manuallyPaused;
        if (playIcon) playIcon.hidden = !manuallyPaused;
      };

      if (pageCount <= 1) {
        root.classList.add('is-static');
        viewport.removeAttribute('tabindex');
      } else {
        if (controls) controls.hidden = false;
        previous?.addEventListener('click', () => goToPage(currentPage - 1));
        next?.addEventListener('click', () => goToPage(currentPage + 1));
        autoplayControl?.addEventListener('click', () => {
          if (paused.has('manual')) resumeFrom('manual');
          else pauseFor('manual');
          updateAutoplayControl();
        });

        viewport.addEventListener('pointerenter', () => pauseFor('pointer'));
        viewport.addEventListener('pointerleave', () => resumeFrom('pointer'));
        root.addEventListener('focusin', () => pauseFor('focus'));
        root.addEventListener('focusout', (event) => {
          if (!root.contains(event.relatedTarget)) resumeFrom('focus');
        });
        viewport.addEventListener('scroll', () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => {
            const positions = Array.from({ length: pageCount }, (_, page) => pageLeft(page));
            currentPage = positions.reduce((closest, position, page) => (
              Math.abs(position - viewport.scrollLeft) < Math.abs(positions[closest] - viewport.scrollLeft) ? page : closest
            ), 0);
            updateStatus(false);
          }, 120);
        }, { passive: true });
      }

      const handleMotionChange = () => {
        viewport.style.scrollBehavior = reducedMotion.matches ? 'auto' : '';
        startAutoplay();
      };

      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', handleMotionChange);
      else reducedMotion.addListener(handleMotionChange);

      const handleVisibilityChange = () => startAutoplay();
      document.addEventListener('visibilitychange', handleVisibilityChange);

      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) resumeFrom('viewport');
          else pauseFor('viewport');
        }, { threshold: 0.2 });
        intersectionObserver.observe(root);
      }

      if ('ResizeObserver' in window && pageCount > 1) {
        resizeObserver = new ResizeObserver(() => {
          viewport.scrollTo({ left: pageLeft(currentPage), behavior: 'auto' });
        });
        resizeObserver.observe(viewport);
      }

      root.mpAwardDestroy = () => {
        destroyed = true;
        stopAutoplay();
        window.clearTimeout(scrollTimer);
        window.clearTimeout(announcementTimer);
        window.cancelAnimationFrame(announcementFrame);
        resizeObserver?.disconnect();
        intersectionObserver?.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', handleMotionChange);
        else reducedMotion.removeListener(handleMotionChange);
      };

      updateAutoplayControl();
      updateStatus(false);
      startAutoplay();
    });
  };

  if (window.mpAwardCarouselInitialise) {
    window.mpAwardCarouselInitialise(document);
    return;
  }

  window.mpAwardCarouselInitialise = initialise;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  } else {
    initialise(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(selector).forEach((root) => root.mpAwardDestroy?.());
  });
})();
